import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from '../../constants/routePaths';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel
} from '@mui/material';
import {
  Save as SaveIcon
} from '@mui/icons-material';
import { toast } from 'sonner';
import { deploymentInstanceService } from '../../services/DeploymentInstanceService';
import { promptLabService } from '../../services/PromptLabService';
import { PromptVariablesPanel } from '../../components/PromptVariablesPanel';
import CodeMirror from '@uiw/react-codemirror';
import { StreamLanguage } from '@codemirror/language';
import { oneDark } from '@codemirror/theme-one-dark';
import { EditorView } from '@codemirror/view';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags as t } from '@lezer/highlight';

// Custom syntax highlighting for variables in curly braces
const variableHighlight = HighlightStyle.define([
  { tag: t.variableName, color: '#4ade80', fontWeight: 'bold' },
  { tag: t.bracket, color: '#fbbf24' },
  { tag: t.string, color: '#60a5fa' },
]);

// Simple tokenizer for {variable} patterns
const promptTemplateLanguage = StreamLanguage.define({
  token: (stream: any) => {
    // Check for variable pattern {variable_name}
    if (stream.match(/\{[a-zA-Z_][a-zA-Z0-9_]*\}/)) {
      return 'variableName';
    }
    // Check for opening brace
    if (stream.match('{')) {
      return 'bracket';
    }
    // Check for closing brace
    if (stream.match('}')) {
      return 'bracket';
    }
    // Move forward one character if no match
    stream.next();
    return null;
  }
});

export function PromptLabAdd() {
  const navigate = useNavigate();
  
  const [instanceId, setInstanceId] = useState('');
  const [instances, setInstances] = useState<any[]>([]);
  const [loadingInstances, setLoadingInstances] = useState(true);
  const [promptName, setPromptName] = useState('');
  const [usageCategory, setUsageCategory] = useState('');
  const [promptType, setPromptType] = useState('system_prompt');
  const [usageTypes, setUsageTypes] = useState<any[]>([]);
  const [loadingUsageTypes, setLoadingUsageTypes] = useState(true);
  const [prompts, setPrompts] = useState<any[]>([]);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [saving, setSaving] = useState(false);
  const [promptVariables, setPromptVariables] = useState<any[]>([]);
  const [loadingPromptVariables, setLoadingPromptVariables] = useState(true);
  const [invalidVariables, setInvalidVariables] = useState<string[]>([]);

  const initialLoading = loadingInstances || loadingUsageTypes;

  // Function to extract variables from prompt text
  const extractVariables = (text: string): string[] => {
    const regex = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;
    const matches = text.matchAll(regex);
    return Array.from(matches, match => match[1]);
  };

  // Function to validate variables
  const validateVariables = (text: string) => {
    const usedVariables = extractVariables(text);
    const validVariableNames = promptVariables.map(v => v.name);
    const invalid = usedVariables.filter(v => !validVariableNames.includes(v));
    setInvalidVariables(invalid);
    return invalid.length === 0;
  };

  useEffect(() => {
    const fetchInstances = async () => {
      try {
        const data = await deploymentInstanceService.getDeploymentInstances();
        const runningInstances = (data || []).filter(
          (instance: any) => instance.instance_status === 'Running'
        );
        setInstances(runningInstances);
      } catch (error) {
        console.error('Failed to load deployment instances:', error);
      } finally {
        setLoadingInstances(false);
      }
    };

    fetchInstances();
  }, []);

  useEffect(() => {
    const fetchUsageTypes = async () => {
      try {
        const data = await promptLabService.getUsageTypes();
        setUsageTypes(data);
      } catch (error) {
        console.error('Failed to load usage types:', error);
      } finally {
        setLoadingUsageTypes(false);
      }
    };

    fetchUsageTypes();
  }, []);

  useEffect(() => {
    const fetchPromptVariables = async () => {
      try {
        const data = await promptLabService.getPromptVariables();
        setPromptVariables(data.prompt_variables || []);
      } catch (error) {
        console.error('Failed to load prompt variables:', error);
      } finally {
        setLoadingPromptVariables(false);
      }
    };

    fetchPromptVariables();
  }, []);

  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        const data = await promptLabService.getPrompts();
        setPrompts(data);
      } catch (error) {
        console.error('Failed to load prompts:', error);
      }
    };

    fetchPrompts();
  }, []);

  // Clear system prompt when usage category changes in add mode
  useEffect(() => {
    if (usageCategory) {
      setSystemPrompt('');
      setInvalidVariables([]);
    }
  }, [usageCategory]);

  // Validate variables when system prompt or available variables change
  useEffect(() => {
    if (systemPrompt && promptVariables.length > 0) {
      validateVariables(systemPrompt);
    }
  }, [systemPrompt, promptVariables]);

  const handleSaveTemplate = async () => {
    if (!promptName.trim()) {
      toast.error('Name is required.');
      return;
    }

    if (!systemPrompt.trim()) {
      toast.error('System prompt is required.');
      return;
    }

    if (!usageCategory) {
      toast.error('Usage category is required.');
      return;
    }

    // Validate variables before saving
    if (invalidVariables.length > 0) {
      toast.error(`Invalid variables used: ${invalidVariables.map(v => `{${v}}`).join(', ')}. Please use only available variables.`);
      return;
    }

    setSaving(true);
    try {
      const selectedType = usageTypes.find(type => type.name === usageCategory);
      if (!selectedType) {
        toast.error('Selected usage type not found.');
        return;
      }

      const formattedPrompt = systemPrompt.split('\n').join('\\n');

      const payload = {
        name: promptName.trim(),
        usage_type_id: selectedType.id,
        system_prompt: formattedPrompt,
        prompt_type: promptType,
        is_active: true
      };

      await promptLabService.savePrompt(payload);
      toast.success('Prompt created successfully!');
      
      navigate(ROUTE_PATHS.PROMPT_LAB);
    } catch (error: any) {
      console.error('Failed to save template:', error);
      toast.error(`Failed to save template: ${error.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    navigate(ROUTE_PATHS.PROMPT_LAB);
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="xl">
        {initialLoading ? (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: '60vh',
              flexDirection: 'column',
              gap: 2
            }}
          >
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ color: '#64748b' }}>
              Loading Template...
            </Typography>
          </Box>
        ) : (
          <>
            {/* Header */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: '#0f172a',
                    mb: 1
                  }}
                >
                  Prompt Template
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#64748b'
                  }}
                >
                  Test and refine your prompts with different parameters
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <SaveIcon />}
                  onClick={handleSaveTemplate}
                  disabled={saving || !promptName.trim() || !systemPrompt.trim() || !usageCategory || invalidVariables.length > 0}
                >
                  {saving ? 'Saving...' : 'Save Template'}
                </Button>
              </Box>
            </Box>

            {/* Form */}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  {/* Name Field */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: '#0f172a',
                        mb: 1
                      }}
                    >
                      Name
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter prompt name"
                      value={promptName}
                      onChange={(e) => setPromptName(e.target.value)}
                      variant="outlined"
                      size="small"
                    />
                  </Box>

                  {/* Usage Category Selection */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: '#0f172a',
                        mb: 1
                      }}
                    >
                      Usage Category
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        labelId="usage-category-label"
                        id="usage-category-select"
                        value={usageCategory}
                        onChange={(e) => setUsageCategory(e.target.value)}
                        disabled={loadingUsageTypes || usageTypes.length === 0}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) {
                            return <em style={{ color: '#94a3b8', fontStyle: 'normal' }}>Select usage category</em>;
                          }
                          const type = usageTypes.find(t => t.name === selected);
                          return type?.name || selected;
                        }}
                      >
                        {loadingUsageTypes ? (
                          <MenuItem key="loading-types" value="" disabled>
                            <CircularProgress size={20} sx={{ mr: 1 }} /> Loading...
                          </MenuItem>
                        ) : usageTypes.length === 0 ? (
                          <MenuItem key="no-types" value="" disabled>
                            No usage types available
                          </MenuItem>
                        ) : (
                          usageTypes.map((type) => (
                            <MenuItem key={type.id} value={type.name}>
                              {type.name}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  </Box>
                  {/* Prompt Type Selection */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: '#0f172a',
                        mb: 1
                      }}
                    >
                      Prompt Type
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        row
                        value={promptType}
                        onChange={(e) => {
                          console.log('Radio button changed to:', e.target.value);
                          setPromptType(e.target.value);
                        }}
                      >
                        <FormControlLabel
                          value="system_prompt"
                          control={<Radio />}
                          label="System Prompt"
                        />
                        <FormControlLabel
                          value="user_prompt"
                          control={<Radio />}
                          label="User Prompt"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>                </Paper>
              </Grid>

              {/* System Prompt and Variables Layout */}
              <Grid size={{ xs: 12, md: promptVariables.length > 0 ? 8 : 12 }}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: '#0f172a'
                        }}
                      >
                        {promptType === 'system_prompt' ? 'System Prompt' : 'User Prompt'}
                      </Typography>
                      {invalidVariables.length > 0 && (
                        <Chip
                          label={`${invalidVariables.length} invalid variable${invalidVariables.length > 1 ? 's' : ''}`}
                          size="small"
                          sx={{
                            bgcolor: '#fee2e2',
                            color: '#991b1b',
                            fontWeight: 600,
                            height: 22
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  {invalidVariables.length > 0 && (
                    <Box 
                      sx={{ 
                        bgcolor: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: 1,
                        p: 1.5,
                        mb: 2
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#991b1b',
                          fontWeight: 600,
                          display: 'block',
                          mb: 0.5
                        }}
                      >
                        ⚠️ Invalid Variables Detected
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#b91c1c',
                          display: 'block'
                        }}
                      >
                        The following variables are not available: {invalidVariables.map(v => `{${v}}`).join(', ')}
                      </Typography>
                    </Box>
                  )}
                  
                  {/* CodeMirror Editor */}
                  <Box
                    sx={{
                      border: '1px solid #e2e8f0',
                      borderRadius: 1,
                      overflow: 'hidden',
                      bgcolor: '#1e293b'
                    }}
                  >
                    {/* Editor Header */}
                    <Box
                      sx={{
                        bgcolor: '#0f172a',
                        px: 2,
                        py: 1,
                        borderBottom: '1px solid #334155',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ef4444' }} />
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#10b981' }} />
                      </Box>
                      <Typography variant="caption" sx={{ color: '#94a3b8', ml: 2 }}>
                        {promptType === 'system_prompt' ? 'system-prompt.txt' : 'user-prompt.txt'}
                      </Typography>
                    </Box>
                    
                    {/* CodeMirror Editor Content */}
                    <CodeMirror
                      value={systemPrompt}
                      height="500px"
                      theme={oneDark}
                      extensions={[
                        promptTemplateLanguage,
                        syntaxHighlighting(variableHighlight),
                        EditorView.baseTheme({
                          '.cm-variableName': {
                            color: '#4ade80 !important',
                            fontWeight: 'bold'
                          }
                        })
                      ]}
                      onChange={(value) => {
                        setSystemPrompt(value);
                      }}
                      placeholder="Select a usage category to load the prompt template...

You can use variables like {variable_name} in your prompt."
                      basicSetup={{
                        lineNumbers: true,
                        highlightActiveLineGutter: true,
                        highlightSpecialChars: true,
                        foldGutter: true,
                        drawSelection: true,
                        dropCursor: true,
                        allowMultipleSelections: true,
                        indentOnInput: true,
                        syntaxHighlighting: true,
                        bracketMatching: true,
                        closeBrackets: true,
                        autocompletion: true,
                        rectangularSelection: true,
                        crosshairCursor: true,
                        highlightActiveLine: true,
                        highlightSelectionMatches: true,
                        closeBracketsKeymap: true,
                        searchKeymap: true,
                        foldKeymap: true,
                        completionKeymap: true,
                        lintKeymap: true,
                      }}
                      style={{
                        fontSize: '14px',
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>

              {/* Prompt Variables Side Panel */}
              {promptVariables.length > 0 && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <PromptVariablesPanel 
                    promptVariables={promptVariables}
                    maxHeight="700px"
                    useSticky={true}
                  />
                </Grid>
              )}
            </Grid>
          </>
        )}
      </Container>
    </Box>
  );
}
